import React, { useEffect, useState } from "react";
import { func, object } from "prop-types";
import { connect } from "react-redux";
import { WithMember } from "@quintype/components";
import ImageUploading from "react-images-uploading";
import { format, parse } from "date-fns";
import { get } from "lodash";

import Button from "../../atoms/Button";
import TextField from "../../atoms/TextField";
import GenderIcon from "../../atoms/icons/gender";
import ProfileHeadline from "../../atoms/ProfileHeadline";
import MessageView from "../../atoms/MessageView";
import ContributorImage from "../../atoms/ContributorImage";
import Plus from "../../atoms/icons/Plus";
import DatePicker from "../../atoms/ReactDatePicker";

import {
  getCountriesList,
  memberProfileUpdate,
  signImage,
  updateUserInformation,
  uploadS3ToTemp,
} from "../../helper/api";
import { setLoading, setMember } from "../../helper/actions";
import { getBirthDate, getMember } from "../../utils/utils";

import "./edit-profile.m.css";

const EditProfile = ({ member, setLoading, navigateToPage, setMember }) => {
  const [error, setError] = useState(null);
  const [name, setName] = useState(member.name || "");
  const [countriesList, setCountriesList] = useState([]);
  const [nameError, setNameError] = useState(null);
  const [metadata, setMetadata] = useState(member.metadata || {});
  const [selectedGender, setSelectedGender] = useState(metadata.gender ? metadata.gender : "male"); // default male
  const dob = getBirthDate(get(member, ["metadata", "dob"]), "dd-MM-yyyy");
  const [birthday, setBirthday] = useState(dob && parse(dob, "dd-MM-yyyy", new Date()));
  const [tempImageObj, setTempImageObj] = useState(null);
  const [blurImage, setBlurImage] = useState(false);

  const memberObj = getMember(member);

  const genderSelection = (type) => {
    setSelectedGender(type);
  };

  const setData = (e) => {
    const metadataObj = { ...metadata };
    const fieldName = e.target.name;
    metadataObj[fieldName] = e.target.value;

    if (e.target.value && metadataObj[`${fieldName}Error`]) {
      metadataObj[`${fieldName}Error`] = "";
    }

    if (error) {
      setError(null);
    }

    setMetadata(metadataObj);
  };

  const nameChange = (e) => {
    if (e.target.value && nameError) {
      setNameError(null);
    }

    if (error) {
      setError(null);
    }

    setName(e.target.value);
  };

  const dateSelected = (newDate) => setBirthday(newDate);

  const onSubmitClick = (e) => {
    if (name.length === 0) {
      return;
    }
    e.preventDefault();

    const data = { ...metadata };
    data.gender = selectedGender;

    if (birthday) {
      const date = new Date(birthday);
      data.dob = format(date, "dd-MM-yyyy");
    }

    setLoading(true);
    updateUserInformation({ metadata: data })
      .then(() => {
        if (member.name !== name) {
          memberProfileUpdate({ name: name }).then((resp) => {
            tempImageObj
              ? setMember({ ...member, ...{ metadata: data }, ...tempImageObj, ...{ name: resp.name } })
              : setMember({ ...member, ...{ metadata: data }, ...resp });
            resp.name !== member.name && navigateToPage("/profile");
          });
        } else {
          setMember({ ...member, ...{ metadata: data }, ...tempImageObj });
          navigateToPage("/profile");
        }
      })
      .catch((ex) => setError(ex.message || "Please try after some time."))
      .finally(() => setLoading(false));
  };

  const prepareFormData = (keys, formdata, imageList, res) => {
    return keys.map((key) => {
      if (key === "file") {
        return formdata.append(key, imageList[0].file);
      } else {
        return formdata.append(key, res[key]);
      }
    });
  };

  // Profile pic change
  const onProfileChange = (imageList) => {
    setBlurImage(true);
    signImage(imageList[0].file.name, imageList[0].file.type).then((res) => {
      const formdata = new FormData();
      const keys = [
        "key",
        "Content-Type",
        "policy",
        "acl",
        "success_action_status",
        "AWSAccessKeyId",
        "signature",
        "file",
      ];

      prepareFormData(keys, formdata, imageList, res);

      uploadS3ToTemp(res, formdata).then((response) => {
        if (response.status === 201) {
          memberProfileUpdate({ s3Key: res.key })
            .then(({ user }) => {
              if (!user) {
                setBlurImage(false);
                return;
              }

              setTempImageObj(user);
              setBlurImage(false);
            })
            .catch(() => {
              setBlurImage(false);
            });
        }
      });
    });
  };

  useEffect(() => {
    getCountriesList().then((resp) => setCountriesList(resp));
  }, []);

  return (
    <WithMember>
      {() => (
        <form onSubmit={(e) => onSubmitClick(e)}>
          <div styleName="container">
            <ProfileHeadline title="Edit Profile" type="edit-profile" arrowClick={() => navigateToPage("/profile")} />
            <ImageUploading onChange={onProfileChange}>
              {({ imageList, onImageUpload, onImageRemoveAll }) => (
                <React.Fragment>
                  <div styleName="profile-pic">
                    <div styleName="with-upload-wrapper">
                      <ContributorImage
                        authorObj={tempImageObj || memberObj}
                        customStyle={true}
                        blurStyle={blurImage}
                        memberProfilePic={true}
                      />
                      <button styleName="upload-image" type="button" onClick={onImageUpload}>
                        <Plus />
                      </button>
                    </div>
                    <span styleName="email-value">{member.email}</span>
                  </div>
                </React.Fragment>
              )}
            </ImageUploading>

            <div styleName="details">
              <span styleName="personal-info">Personal Information</span>

              <div styleName="gender-wrapper">
                <span styleName="gender-label">Gender</span>
                <div styleName="gender">
                  <div
                    styleName={`male-view ${selectedGender === "male" ? "selected-gender" : "unselected-gender"}`}
                    onClick={() => genderSelection("male")}
                  >
                    <GenderIcon type="male" />
                  </div>
                  <div
                    styleName={`female-view ${selectedGender === "female" ? "selected-gender" : "unselected-gender"}`}
                    onClick={() => genderSelection("female")}
                  >
                    <GenderIcon type="female" />
                  </div>
                </div>
              </div>

              <div styleName="column-fields">
                <TextField
                  value={name}
                  error={nameError}
                  label="Full Name"
                  name="name"
                  inputType="type2"
                  onChange={nameChange}
                  required
                />
                <div>
                  <label styleName="country-label">Country / Region</label>
                  <select styleName="countries-list" name="address" value="address" onChange={setData}>
                    <option value={metadata.address}>{metadata.address}</option>
                    {countriesList.length > 0
                      ? countriesList.map((item, index) => (
                          <option key={index} value={item}>
                            {item}
                          </option>
                        ))
                      : null}
                  </select>
                </div>
              </div>

              <div styleName="column-fields">
                <TextField
                  value={metadata["contact-number"]}
                  error={metadata["contact-numberError"]}
                  label="Phone Contact"
                  name="contact-number"
                  inputType="type2"
                  onChange={setData}
                  type="number"
                  styleName="contact-number"
                />

                <div styleName="birthday-view">
                  <label styleName="datepicker-label">Birthday</label>
                  <DatePicker
                    onDateSelect={dateSelected}
                    src="edit-profile"
                    startPlaceholder="Date of Birth"
                    fromDate={birthday}
                  />
                </div>
              </div>

              <MessageView type="error" text={error} />

              <div styleName="column-fields">
                <Button content="SAVE" buttonType="type4" type="submit" />
                <div />
              </div>
            </div>
          </div>
        </form>
      )}
    </WithMember>
  );
};

EditProfile.propTypes = {
  member: object.isRequired,
  navigateToPage: func,
  setLoading: func,
  setMember: func,
};

const mapDispatchToProps = (dispatch) => ({
  setMember: (member) => dispatch(setMember(member)),
  navigateToPage: (url) => global.app.navigateToPage(dispatch, url),
  setLoading: (isLoading) => dispatch(setLoading(isLoading)),
});

export default connect(null, mapDispatchToProps)(EditProfile);
